import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Clients } from '@ariksa/notification';
import { ClientResponse } from '@ariksa/notification/api';
import { Box, Center, Grid, GridItem, HStack, Stack } from '@chakra-ui/react';
import { each, isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { IdentityProviderName } from 'api/auth/api.pb';
import {
  Card,
  CustomTooltip,
  getIcon,
  PageHeaderWithIcon,
} from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { AddOrCreateIcon, DependenciesIcon } from 'components/Icons';
import { usePageContentContext } from 'components/Layout';
import { selectApp } from 'containers/App/selectors';
import { GettingStartedWithThirdPartyIntegration } from 'containers/Setup/Integrations/Components/Utils/GettingStartedWithThirdPartyIntegration';
import {
  IntegrationsContextProps,
  IntegrationsContextProvider,
} from 'containers/Setup/Integrations/context/context';
import { useIntegrations } from 'containers/Setup/Integrations/hooks/useIntegrations';
import { selectIntegrations } from 'containers/Setup/Integrations/selectors';
import { actions } from 'containers/Setup/Integrations/slice';
import { selectSharedState } from 'containers/SharedState/selectors';

export const IntegrationCards = () => {
  const { allClients, sso } = useSelector(selectIntegrations);
  const { isDevOrTest } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const { cloudAccounts } = useSelector(selectApp);
  const navigate = useNavigate();

  const contextProps: IntegrationsContextProps = useIntegrations();
  const { contentHeight } = usePageContentContext();

  const [hasAccounts, setHasAccounts] = useState(true);
  const [hasClients, setHasClients] = useState(true);
  const [hasSso, setHasSso] = useState(true);
  const [clientsMapping, setClientsMapping] = useState<Record<string, any>>({});
  const [ssoMapping, setSsoMapping] = useState<Record<string, any>>({});
  const [accountMapping, setAccountMapping] = useState<Record<string, any>>({});

  const integrationCards = useMemo(
    () => [
      {
        label: 'Elastic',
        category: 'SIEM',
        path: Clients.Elasticsearch,
        description:
          'Elastic is a data aggregation' +
          ' solution. Integrate to ingest issues, vulnerabilities, insights, ' +
          'and inventory for long-term retention and search using Elastic queries.',
        addIconTooltip: 'Add Elastic',
        type: 'client',
        clientName: Clients.Elasticsearch,
      },
      {
        label: 'JIRA',
        category: 'Ticketing',
        path: Clients.Jira,
        description:
          'Atlassian JIRA is a ticketing platform used to track and manage security ' +
          'response. Integrate to auto-create and track status of tickets.',
        addIconTooltip: 'Add JIRA project',
        type: 'client',
        clientName: Clients.Jira,
      },
      {
        label: 'ServiceNow',
        category: 'Ticketing',
        path: Clients.Servicenow,
        description:
          'ServiceNow is a workflow management platform used to track security efforts. ' +
          'Integrate to auto-create and track status of tickets.',
        addIconTooltip: 'Add ServiceNow project',
        type: 'client',
        clientName: Clients.Servicenow,
      },
      {
        label: 'Slack',
        category: 'Notification',
        path: Clients.Slack,
        description:
          'Slack is a cloud-based messaging system used for communication and ' +
          'collaboration. Integrate to send notifications for issues or insights ' +
          'to accelerate response.',
        addIconTooltip: 'Add Slack channel',
        type: 'client',
        clientName: Clients.Slack,
      },
      {
        label: 'Microsoft Teams',
        category: 'Notification',
        path: Clients.Teams,
        description:
          'Microsoft Team is a team collaboration and communication app. ' +
          'Integrate to send notifications for issues or insights to accelerate response.',
        addIconTooltip: 'Add Teams channel',
        type: 'client',
        clientName: Clients.Teams,
      },
      {
        label: 'SMS',
        category: 'Notification',
        path: Clients.Sns,
        description:
          'Short Messaging Service (SMS) can be used to exchange short message for ' +
          'issues and incident response',
        addIconTooltip: 'Add SMS',
        type: 'client',
        clientName: Clients.Sns,
      },
      {
        label: 'Okta',
        category: 'SSO',
        path: 'sso/' + IdentityProviderName.Okta,
        description:
          'Okta provides single sign-on for SaaS apps. Integrate to unify ' +
          'authentication and to allow your users to login to Ariksa using Okta ' +
          'credentials.',
        addIconTooltip: 'Add Okta SSO',
        type: 'sso',
        clientName: IdentityProviderName.Okta,
      },
      {
        label: 'Google Workspace',
        category: 'SSO',
        path: 'sso/' + IdentityProviderName.Google,
        description:
          'Google SSO (Single Sign-On) allows users to access multiple applications, ' +
          'including Google services, using a single set of credentials, ' +
          'typically a Google account.',
        addIconTooltip: 'Add Google Workspace SSO',
        type: 'sso',
        clientName: IdentityProviderName.Google,
      },
      {
        label: 'Entra ID',
        category: 'SSO',
        path: 'sso/' + IdentityProviderName.Microsoft,
        description:
          'Entra ID provides single sign-on for SaaS apps. Integrate to unify ' +
          'authentication and to allow your users to login to Ariksa using ' +
          'Entra ID credentials.',
        addIconTooltip: 'Add Entra ID SSO',
        type: 'sso',
        clientName: IdentityProviderName.Microsoft,
      },
      {
        label: 'Email Group',
        category: 'Notification',
        path: Clients.Email,
        description:
          'Integrate with email to receive notifications for issues that need ' +
          'attention and for insights and observability.',
        addIconTooltip: 'Add Email Group',
        type: 'client',
        clientName: Clients.Email,
      },
      {
        label: 'GitLab',
        category: 'Repository',
        path: CloudProviders.Gitlab,
        description:
          'GitLab is a code repository and DevOps platform. Integrate to ' +
          'scan it for sensitive data, and exposed credentials that may be ' +
          'used for unauthorized access.',
        addIconTooltip: 'Add GitLab',
        type: 'saas',
        clientName: CloudProviders.Gitlab,
      },
      {
        label: 'GitHub',
        category: 'Repository',
        path: CloudProviders.Github,
        description:
          'GitHub is a code repository and DevOps platform. Integrate to ' +
          'scan it for sensitive data, and exposed credentials that may be ' +
          'used for unauthorized access.',
        addIconTooltip: 'Add GitHub',
        type: 'saas',
        clientName: CloudProviders.Github,
      },
      {
        label: 'Bitbucket',
        category: 'Repository',
        path: CloudProviders.Bitbucket,
        description:
          'Atlassian Bitbucket is a code repository and DevOps platform. ' +
          'Integrate to scan it for sensitive data, and exposed credentials ' +
          'that can be used for attacks.',
        addIconTooltip: 'Add Bitbucket',
        type: 'saas',
        clientName: CloudProviders.Bitbucket,
      },
      ...(isDevOrTest
        ? [
            {
              label: 'Tableau',
              category: 'Analytics',
              path: 'tableau',
              description:
                'Tableau is a visual analytics platform. Integrate to gain visibility ' +
                'into data sources and dashboards.',
              addIconTooltip: 'Add Tableau',
              type: 'saas',
              clientName: 'tableau',
            },
          ]
        : []),
    ],
    [isDevOrTest],
  );

  useEffect(() => {
    dispatch(
      actions.getAllClients({
        q: {},
        onSuccess: res => {
          let mapping: Record<string, ClientResponse[]> = {};
          each(res, o =>
            !!mapping[o.client_name]
              ? mapping[o.client_name]?.push(o)
              : (mapping[o.client_name] = [o]),
          );
          setClientsMapping(mapping);
          setHasClients(!isEmpty(res));
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const validCloudTypes = new Set<CloudProviders>([
      CloudProviders.Gitlab,
      CloudProviders.Github,
      CloudProviders.Bitbucket,
    ]);
    const accounts = cloudAccounts.data?.some(o =>
      validCloudTypes.has(o?.cloud_type!),
    );
    setHasAccounts(accounts);

    let mapping: Record<string, any[]> = {};
    each(cloudAccounts.data, o =>
      !!mapping[o.cloud_type!]
        ? mapping[o.cloud_type!]?.push(o)
        : (mapping[o.cloud_type!] = [o]),
    );
    setAccountMapping(mapping);
  }, [cloudAccounts.data]);

  useEffect(() => {
    dispatch(actions.getSSOProviders());
  }, [dispatch]);

  useEffect(() => {
    let mapping: Record<string, any[]> = {};
    each(sso.providers.data, o =>
      !!mapping[o.provider!]
        ? mapping[o.provider!]?.push(o)
        : (mapping[o.provider!] = [o]),
    );
    setSsoMapping(mapping);
    setHasSso(!isEmpty(sso.providers.data));
  }, [sso.providers.data]);

  const getMapping = useCallback(
    (entryType, integrationType) => {
      switch (entryType) {
        case 'client':
          return clientsMapping?.[integrationType]?.length ?? 0;
        case 'sso':
          return ssoMapping?.[integrationType]?.length ?? 0;
        case 'saas':
          return accountMapping?.[integrationType]?.length ?? 0;
        default:
          return 0;
      }
    },
    [clientsMapping, accountMapping, ssoMapping],
  );

  return cloudAccounts.loading ? (
    <CustomSpinner size="lg" />
  ) : hasClients || hasAccounts || hasSso ? (
    <Card styles={{ card: { h: contentHeight - 8 + 'px' } }}>
      <IntegrationsContextProvider value={contextProps}>
        <Stack spacing={5} w="full">
          <Box>
            <PageHeaderWithIcon
              label="Integrations"
              icon={<DependenciesIcon />}
            />
          </Box>
          <Grid
            w="full"
            flex={1}
            //h="full"
            templateColumns="repeat(6, 1fr)"
            gap={3}
            overflowY="auto"
          >
            {map(integrationCards, o => (
              <GridItem>
                <Box
                  border="0.5px solid"
                  borderColor={customTheme.colors.gray['200']}
                  p={3}
                  borderRadius={6}
                  h="300px"
                  cursor="pointer"
                  onClick={() => navigate('/setup/integrations/' + o?.path)}
                >
                  <Stack spacing={3} position="relative" h="full">
                    <HStack justify="space-between" align="flex-start">
                      <HStack align="flex-start" spacing={4}>
                        <Box boxSize={9} p={1}>
                          <Center>
                            {getIcon(o?.iconType ?? o?.clientName)}
                          </Center>
                        </Box>
                        <Stack spacing={0}>
                          <Box fontWeight={600} fontSize="lg">
                            {o?.label}
                          </Box>
                          <Box
                            fontSize="md"
                            color={customTheme.colors.gray['250']}
                          >
                            {o?.category}
                          </Box>
                        </Stack>
                      </HStack>
                      <Box
                        boxSize={6}
                        cursor="pointer"
                        onClick={e => {
                          e.stopPropagation();
                          navigate('/setup/integrations/add/' + o?.path);
                        }}
                      >
                        <Center>
                          <CustomTooltip
                            label={o?.addIconTooltip}
                            placement="bottom-end"
                            w={24}
                            textAlign="center"
                          >
                            <AddOrCreateIcon />
                          </CustomTooltip>
                        </Center>
                      </Box>
                    </HStack>
                    <Box overflowY="auto" pl={2} pt={3}>
                      {o?.description}
                    </Box>
                    <Box position="absolute" bottom={0} left={0} w="full">
                      {(o?.type === 'client' && allClients.isLoading) ||
                      (o?.type === 'sso' && sso.providers?.loading) ? (
                        <Box>
                          <CustomSpinner />
                        </Box>
                      ) : (
                        <Box
                          pl={2}
                          color={
                            getMapping(o?.type, o?.clientName)
                              ? 'primary'
                              : customTheme.colors.gray['250']
                          }
                        >
                          {getMapping(o?.type, o?.clientName)} entr
                          {getMapping(o?.type, o?.clientName) === 1
                            ? 'y'
                            : 'ies'}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </GridItem>
            ))}
          </Grid>
          {/*<SideNav
            menuItems={integrationsMenuItems}
            current={currentTab}
            onChange={tab => {
              onCancel();
              dispatch(actions.updateCurrentTab(tab));
            }}
          />*/}
        </Stack>
      </IntegrationsContextProvider>
    </Card>
  ) : (
    <GettingStartedWithThirdPartyIntegration
      setHasClients={val => setHasClients(val)}
    />
  );
};
